import { EntityType } from "./entityType.js";
import type { Recommendation } from "./models/Recommendation.js";

/**
 * Recommendations Hub are mainly assistant prompt suggestions, but it could evolve to be
 * quick actions, visiting/navigating to a page, creating some entities, etc.
 */
export const defaultRecommendations: Recommendation[] = [
  // ============================================================================
  // HELP & ONBOARDING
  // ============================================================================

  {
    id: "integrations-get-help",
    label: "Learn how to use NOAN",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Note,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me learn how to use NOAN. Search the knowledge base for tutorials and help articles about specific features or tasks.",
      includeContext: { facts: true, recentActivity: true },
    },
  },

  // ============================================================================
  // QUICK ACTION RECOMMENDATIONS - MOST COMMON ENTITY CREATION
  // ============================================================================

  {
    id: "action-create-contact",
    label: "Add a contact",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Contact,
    payload: {
      type: "template-prompt",
      action: "create-contact",
      prompt:
        "Add a contact to my network. Example: 'Add John Doe, john.doe@techcorp.com, VP of Engineering at TechCorp. Met at AI Summit 2024, he's interested in our API platform. Tag as: potential-partner, engineering-lead'",
    },
  },
  {
    id: "action-create-task",
    label: "Create a task",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Task,
    payload: {
      type: "template-prompt",
      action: "create-task",
      prompt:
        "Create a task for me. Example: 'Follow up with John Doe about partnership proposal' with details 'Schedule call next week to discuss Q2 collaboration opportunities'",
    },
  },
  {
    id: "action-create-note",
    label: "Create a note",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Note,
    payload: {
      type: "template-prompt",
      action: "create-note",
      prompt:
        "Create a note for me. Example: 'Meeting insights from Q4 planning session: Focus on customer retention (85% target), expand to 3 new markets (EU, APAC, LATAM), hire 5 senior engineers by March'",
    },
  },
  {
    id: "action-create-tag",
    label: "Create a tag",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Tag,
    payload: {
      type: "template-prompt",
      action: "create-tag",
      prompt:
        'Create a tag for organizing content. Example: \'Create a "High Priority" tag in the "Status" category with usage instructions: Use for urgent items requiring immediate attention. Applies to: Contact, Note, Task, Asset\'',
    },
  },

  // ============================================================================
  // GENERAL CONTENT ACTIONS - MOST FREQUENTLY USED
  // ============================================================================

  {
    id: "general-summarize",
    label: "Summarize this for me",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Summarize the content I'm looking at in a clear and concise way.",
      includeContext: { currentPage: true },
    },
  },
  {
    id: "general-improve",
    label: "Improve this content",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review and improve the content I'm looking at. Make it more clear, compelling, and professional.",
      includeContext: { currentPage: true },
    },
  },
  {
    id: "general-explain",
    label: "Explain this to me",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Explain what this content means and its purpose in simple terms.",
      includeContext: { currentPage: true },
    },
  },

  // ============================================================================
  // BLOCK EDITING ACTIONS
  // ============================================================================

  {
    id: "block-expand",
    label: "Expand with more details",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Expand this block with more relevant details and information based on our company facts.",
      includeContext: { currentPage: true, facts: true },
    },
  },
  {
    id: "block-make-shorter",
    label: "Make this shorter",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Condense this block to make it more concise while keeping the key information.",
      includeContext: { currentPage: true },
    },
  },
  {
    id: "block-alternative-versions",
    label: "Generate alternative versions",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create 3 alternative versions of this block with different tones or approaches.",
      includeContext: { currentPage: true, facts: true },
    },
  },

  // ============================================================================
  // CONTACTS - WORKFLOW ORDER
  // ============================================================================

  {
    id: "contacts-add-memo",
    label: "Add memo to contact",
    contextKey: ["assistant"],
    entityType: EntityType.Contact,
    payload: {
      type: "template-prompt",
      action: "create-note",
      prompt:
        "Add a memo or note to an existing contact. Example: 'Add note to Sarah Chen: Great conversation about Q2 marketing strategy. She suggested focusing on video content and offered to introduce us to her content team. Follow up next week.'",
    },
  },
  {
    id: "contacts-outreach-template",
    label: "Create outreach email",
    contextKey: ["assistant"],
    entityType: EntityType.Contact,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create a personalized outreach email for a specific contact in my network. Look up their details and background, then draft an email based on our company information.",
      includeContext: { facts: true },
    },
  },
  {
    id: "contacts-follow-up",
    label: "Draft follow-up message",
    contextKey: ["assistant"],
    entityType: EntityType.Contact,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Draft a follow-up message for a contact. Search for relevant context from our previous interactions and create a personalized message.",
      includeContext: { facts: true, recentActivity: true },
    },
  },
  {
    id: "contacts-search-by-interest",
    label: "Find contacts by interest",
    contextKey: ["assistant"],
    entityType: EntityType.Contact,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Search my contacts by a specific interest, topic, or context. Examples: 'Find all contacts interested in AI', 'Who did I meet at conferences?', 'Find contacts in the healthcare industry'.",
      includeContext: { recentActivity: true },
    },
  },

  // ============================================================================
  // NOTES - WORKFLOW ORDER
  // ============================================================================

  {
    id: "notes-meeting-summary",
    label: "Summarize meeting notes",
    contextKey: ["assistant"],
    entityType: EntityType.Note,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review my recent notes and organize them into a clear summary with action items and key decisions. Save the summary and create tasks for follow-up actions.",
      includeContext: { currentPage: true, recentActivity: true },
    },
  },
  {
    id: "notes-extract-insights",
    label: "Extract key insights",
    contextKey: ["assistant"],
    entityType: EntityType.Note,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review my recent notes, analyze them, and extract the most important insights and learnings. Create a summary note with the key takeaways.",
      includeContext: { currentPage: true, recentActivity: true },
    },
  },
  {
    id: "notes-create-action-plan",
    label: "Create action plan from notes",
    contextKey: ["assistant"],
    entityType: EntityType.Task,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review my notes and turn them into actionable tasks. Prioritize the tasks and add relevant details to each one.",
      includeContext: { currentPage: true },
    },
  },

  // ============================================================================
  // CONTENT CREATION - ASSETS
  // ============================================================================

  {
    id: "assistant-create-content",
    label: "Create marketing content",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me create marketing content based on our company information. Research current industry trends and best practices, then create compelling content.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assets-website-hero",
    label: "Create hero section",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create a compelling hero section for our website that immediately captures attention and communicates our value.",
      includeContext: { facts: true },
    },
  },
  {
    id: "create-website-copy",
    label: "Website copy",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create compelling website copy for our homepage that highlights our value proposition.",
      includeContext: { facts: true },
    },
  },
  {
    id: "create-email-campaign",
    label: "Email campaign",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Draft an email campaign to engage our target audience and promote our offerings.",
      includeContext: { facts: true },
    },
  },
  {
    id: "integrations-email-campaign",
    label: "Plan email campaign",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me plan an email campaign with subject lines, sequences, and content for each email. Research email best practices and create a comprehensive plan.",
      includeContext: { facts: true },
    },
  },
  {
    id: "integrations-gmail-draft",
    label: "Draft email response",
    contextKey: ["assistant", "workspace-onboarding"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me draft a professional email response based on our company brand and information. Save it as a note or provide it directly.",
      includeContext: { facts: true, currentPage: true },
    },
  },
  {
    id: "create-social-posts",
    label: "Social media posts",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Generate a series of social media posts that showcase our brand and engage our audience.",
      includeContext: { facts: true },
    },
  },
  {
    id: "create-blog-post",
    label: "Blog post",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Write a blog post that demonstrates our expertise and provides value to our target audience.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assets-pitch-deck",
    label: "Create pitch deck content",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me create content for a pitch deck including problem, solution, market opportunity, and business model. Use web search to find current market data and trends.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assets-case-study",
    label: "Write a case study",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create a case study that showcases how we've helped a customer achieve results.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assets-ad-copy",
    label: "Generate ad copy",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create advertising copy for different platforms (Google Ads, LinkedIn, Facebook) that drives conversions. Use web search to research current best practices for ad copy on each platform.",
      includeContext: { facts: true },
    },
  },
  {
    id: "create-product-description",
    label: "Product description",
    contextKey: ["assistant"],
    entityType: EntityType.Asset,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Create a compelling product description that highlights features, benefits, and value.",
      includeContext: { facts: true },
    },
  },

  // ============================================================================
  // FACTS & STRATEGIC PLANNING
  // ============================================================================

  {
    id: "facts-review-all",
    label: "Review all company facts",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review all our company facts and tell me what's complete, what's missing, and what could be improved. Give me a comprehensive overview of our business information.",
      includeContext: { facts: true },
    },
  },
  {
    id: "facts-update-foundations",
    label: "Update foundation facts",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me review and improve our foundational facts like mission, vision, and value proposition. Make suggestions based on our company context and recent activity.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assistant-update-facts",
    label: "Update company facts",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review all our company facts and help me identify which ones need updating based on recent changes and activity. Show me what's available and suggest improvements.",
      includeContext: { facts: true, recentActivity: true },
    },
  },
  {
    id: "assistant-brainstorm",
    label: "Brainstorm ideas",
    contextKey: ["assistant"],
    entityType: EntityType.Note,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me brainstorm ideas for our business. Research current trends and opportunities in our industry, then generate creative ideas and actionable next steps.",
      includeContext: { facts: true },
    },
  },
  {
    id: "assistant-analyze",
    label: "Analyze our positioning",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Analyze our company positioning, mission, and value proposition. Research competitors and market positioning strategies, then provide insights on how we can improve.",
      includeContext: { facts: true },
    },
  },
  {
    id: "facts-competitive-analysis",
    label: "Add competitive insights",
    contextKey: ["assistant"],
    entityType: EntityType.Block,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Research current competitors and market trends in our industry, then help me document our competitive landscape and what differentiates us.",
      includeContext: { facts: true },
    },
  },

  // ============================================================================
  // TAGS & ORGANIZATION
  // ============================================================================

  {
    id: "tags-suggest-system",
    label: "Suggest tagging system",
    contextKey: ["assistant"],
    entityType: EntityType.Tag,
    payload: {
      type: "template-prompt",
      action: "create-tag",
      prompt:
        "Help me create a comprehensive tagging system for organizing my content. Suggest relevant tag names with categories, usage instructions, and which types of content they apply to (Contact, Note, Asset, etc.). Example: Create 'High Priority' tag for urgent items, 'Investor' tag for investor contacts.",
    },
  },
  {
    id: "tags-organize-existing",
    label: "Organize existing tags",
    contextKey: ["assistant"],
    entityType: EntityType.Tag,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review my existing tags and suggest how to better organize them. Add categories, improve usage instructions, and clarify which content types they apply to.",
      includeContext: { recentActivity: true },
    },
  },
  {
    id: "tags-apply-to-contacts",
    label: "Tag contacts by criteria",
    contextKey: ["assistant"],
    entityType: EntityType.Contact,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Find contacts matching specific criteria and apply relevant tags to them. Examples: 'Tag all investors', 'Tag contacts from tech companies', 'Tag people I met at conferences'.",
      includeContext: { currentPage: true, facts: true },
    },
  },
  {
    id: "tags-cleanup-duplicates",
    label: "Clean up duplicate tags",
    contextKey: ["assistant"],
    entityType: EntityType.Tag,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Review my tags and identify duplicates or similar tags that should be consolidated to keep my tagging system clean and consistent.",
      includeContext: { recentActivity: true },
    },
  },

  // ============================================================================
  // MEMORIES & CONTEXT
  // ============================================================================

  {
    id: "memories-save-preference",
    label: "Save important context",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "template-prompt",
      action: "create-note",
      prompt:
        "Remember this important preference or context for future reference. Example: 'Remember that I prefer direct communication and brief summaries' or 'Remember that we decided to focus on enterprise clients in Q1'.",
    },
  },
  {
    id: "memories-recall-context",
    label: "Recall relevant context",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "assistant-prompt",
      prompt:
        "What relevant context and past interactions do you remember that could help with my current task? Surface memories relevant to what I'm working on.",
      includeContext: { facts: true, recentActivity: true },
    },
  },
  {
    id: "memories-review-all",
    label: "Review saved memories",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Show me all saved memories and context from our past interactions. Help me identify what's still relevant and what could be removed.",
      includeContext: { facts: true, recentActivity: true },
    },
  },
  {
    id: "memories-research-customer-fit",
    label: "Research customer fit (URL)",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Add a new memory that when I say 'research' and include a URL, search that URL, analyze their business model and brand, then compare to our Ideal Customer Profile. Provide a 1-5 score for customer fit, list potential use cases for our product, and outline likely objections to the product.",
    },
  },
  {
    id: "memories-investor-check",
    label: "Investor check (URL)",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Add a new memory that whenever I say 'investor check' search the url I provide, analyze the investor's website, create a summary of their fund, analyze their thesis where available, search their current portfolio and create an overview of the companies in it. Then score our company out of 5 as a fit for their fund and outline any objections they might have.",
    },
  },
  {
    id: "memories-morning-email-summary",
    label: "Morning email summary",
    contextKey: ["assistant"],
    entityType: EntityType.Memory,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Add a memory that whenever I say 'Morning', search my emails and provide a summary of anything I should be aware of.",
    },
  },

  // ============================================================================
  // DOCUMENTS & MEETINGS
  // ============================================================================

  {
    id: "integrations-search-documents",
    label: "Search my documents",
    contextKey: ["assistant"],
    entityType: EntityType.Document,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Search through my uploaded documents and find relevant excerpts and information. Show me all available documents or search for specific content.",
      includeContext: { facts: true },
    },
  },
  {
    id: "action-list-documents",
    label: "View my documents",
    contextKey: ["assistant"],
    entityType: EntityType.Document,
    payload: {
      type: "template-prompt",
      action: "list-documents",
      prompt: "List my documents",
    },
  },
  {
    id: "integrations-calendar-prep",
    label: "Prepare for upcoming meeting",
    contextKey: ["assistant"],
    entityType: EntityType.Note,
    payload: {
      type: "assistant-prompt",
      prompt:
        "Help me prepare for an upcoming meeting. Gather relevant context, create an agenda, prepare talking points, and search for relevant documents.",
      includeContext: { facts: true, recentActivity: true },
    },
  },
];
